// /faq: the Pixally FAQ hub.
//
// The short, quotable answers to the basic questions about Pixally, for people
// and for answer engines. In the GEO baseline (docs/geo/baseline-2026-09-29.md)
// Claude confused Pixally with an unrelated link shortener and could not find the
// CRM; the "What is Pixally" group exists to settle that.
//
// Every answer comes from somewhere already published, and must change when its
// source does:
// - PIXALLY_DEFINITION (lib/siteMeta.js) and config/launch.js
// - config/planComparison.js and config/pricingPlans.js (prices, fees, storage,
//   video packs, access windows). Billed yearly is the monthly price x 10.
//   The Free plan's website videos (3, up to 10 minutes and 2 GB each) are
//   freeWebsiteVideos in config/pricingPlans.js. Ryan, 2026-10-06: the Pixally
//   logo in the website player is not mentioned on the site.
// - the legal pages in content/legal (Terms 5.7 and 6.9, the Privacy Policy, the
//   Client Privacy Notice section 12, the Contractor Terms and Privacy Notice)
// - the founder story already on the site (pages/AboutPage.jsx, PreLaunchPage.jsx)
// No "at launch" labels (Ryan, 1 Oct 2026): the launch date is answered once, in
// "When does Pixally launch?". ⛔ That and "Can I use Pixally now?" go stale on
// January 1, 2027.

import { PIXALLY_DEFINITION } from '../../lib/siteMeta.js';

export const ROUTE = '/faq';
export const UPDATED_AT = '2026-10-11';
export const KICKER = 'FAQ';
export const HERO_ART = 'options';

export const TITLE = 'Pixally FAQ';
export const SEO_TITLE = 'Pixally FAQ: Plans, Pricing, Galleries, Film Delivery and Second Shooters';
export const SEO_DESCRIPTION =
  'Answers about Pixally, the CRM for wedding photographers and videographers: what it is, when it launches, plans and fees, galleries and film delivery, second shooters and switching.';

export const LEDE = PIXALLY_DEFINITION;

export const BODY = [
  { type: 'heading', level: 2, text: 'Comparisons' },
  {
    type: 'paragraph',
    text: 'How Pixally compares with the tools wedding studios use today, with every fact about the other product taken from its own website:',
  },
  {
    type: 'list',
    items: [
      'CRMs: [HoneyBook](/compare/honeybook), [Dubsado](/compare/dubsado), [Studio Ninja](/compare/studio-ninja) and [VSCO Workspace (formerly Táve)](/compare/vsco-workspace)',
      'Galleries and delivery: [Pixieset](/compare/pixieset), [Pic-Time](/compare/pic-time), [ShootProof](/compare/shootproof) and [VidFlow](/compare/vidflow)',
      'Round-ups: [HoneyBook alternatives for wedding photographers](/compare/honeybook-alternatives), [the best CRM for wedding videographers](/compare/best-crm-for-wedding-videographers) and [Pixieset vs Pic-Time vs ShootProof](/compare/client-gallery-platforms)',
    ],
  },
];

export const FAQ_GROUPS = [
  {
    title: 'What Pixally is',
    items: [
      { q: 'What is Pixally?', a: PIXALLY_DEFINITION },
      {
        q: 'Who is Pixally for?',
        a: 'Wedding photographers, wedding videographers and studios that do both, along with the second shooters, associates and editors who work with them.',
      },
      {
        q: 'When does Pixally launch?',
        a: 'January 1, 2027.',
      },
      {
        q: 'Can I use Pixally now?',
        a: 'Not yet. Pixally launches on January 1, 2027, and you can join the waitlist at pixally.com.',
      },
      {
        q: 'Who makes Pixally?',
        a: 'Pixally LLC. It was founded by Ryan Geldermann, a wedding filmmaker who has been filming weddings since 2009, runs the wedding photo and video company Rain On Your Wedding Day, and founded the wedding video editing company Uncut Gems.',
      },
      {
        q: 'Is Pixally the same as the Pixally link shortener?',
        a: 'No. Pixally at pixally.com is a CRM for wedding photographers and videographers. An unrelated link-shortening product has used the same name, and the two have no connection.',
      },
      {
        q: 'Where is Pixally based?',
        a: "In the United States. Pixally LLC's mailing address is in Baltimore, Maryland.",
      },
    ],
  },
  {
    title: 'Plans and pricing',
    items: [
      {
        q: 'What plans does Pixally have?',
        a: 'Four: Free, Basic, Pro and Studio.',
      },
      {
        q: 'How much does Pixally cost?',
        a: 'Free costs nothing. Billed monthly, Basic is $39, Pro $69 and Studio $119 a month. Billed yearly you get two months free: $390, $690 and $1,190 a year, about $33, $58 and $99 a month.',
      },
      {
        q: 'Is there a free trial?',
        a: 'Yes. Every account starts with a 14-day free trial, and no credit card is needed. When the trial ends, the account moves to the Free plan, and you can choose a paid plan at any time.',
      },
      {
        q: "What is included in Pixally's free plan?",
        a: 'Up to 5 active booked projects, with unlimited clients and contacts, lead forms, proposals, contracts with e-signature, questionnaires, email templates and the client portal. It also includes 3 films for your own website, up to 10 minutes and 2 GB each. It does not include gallery storage or video delivery.',
      },
      {
        q: 'How much do extra team members cost?',
        a: 'Free and Basic include one team member and Pro includes two; more cost $9 a month each. Studio includes unlimited team members. Second shooters and contractors do not need team seats: they have their own free contractor accounts.',
      },
      {
        q: 'Does Pixally put its branding on my client pages?',
        a: 'No. There is no "Powered by Pixally" branding on client galleries or the client portal, on any plan.',
      },
      {
        q: 'Can I run more than one brand?',
        a: 'Yes. Free and Basic include one brand, Pro two, and Studio unlimited brands.',
      },
    ],
  },
  {
    title: 'Payments and fees',
    items: [
      {
        q: 'What does Pixally charge to process card payments?',
        a: 'In the US, 2.9% + 30¢ per card payment on Basic, Pro and Studio, and 3.4% + 30¢ on Free. Payments are processed by Stripe.',
      },
      {
        q: 'What does a bank transfer cost?',
        a: '1% on Free and Basic, with no cap. 0.8% on Pro, capped at $12 a payment, and 0.8% on Studio, capped at $8.',
      },
      {
        q: 'Is there a surcharge for Amex, Discover or saved cards?',
        a: 'No. Amex and Discover cost the same as other cards, and there is no extra fee for autopay or card-on-file payments.',
      },
    ],
  },
  {
    title: 'Galleries and film delivery',
    items: [
      {
        q: 'Does Pixally have client galleries?',
        a: 'Yes. Photo, video and hybrid galleries are built in on Basic, Pro and Studio, with 200 GB, 1 TB or unlimited photo storage.',
      },
      {
        q: 'How does video delivery work?',
        a: 'Every studio starts with 50 GB of client video delivery, once, when it first pays for a plan. After that, video comes in packs of 50 GB for $50, 100 GB for $95 or 500 GB for $450. Packs never expire, are shared by every brand in the studio, and can be refunded within 14 days if you still have at least as many unused gigabytes as the pack holds.',
      },
      {
        q: 'Is there a limit on film length?',
        a: 'No. A film can be any length.',
      },
      {
        q: 'How long do delivered photos and films stay live?',
        a: "Photos stay live for as long as you are subscribed. Films stay live for two years from the gallery's first delivery, and the couple can extend that.",
      },
      {
        q: 'Can couples stream and download in 4K?',
        a: 'Yes. Couples can stream and download the full 4K film for as long as the gallery is live, and the camera original is available for the first 90 days after delivery.',
      },
      {
        q: 'Can I host films on my own website?',
        a: 'Yes. Free includes 3 films of up to 10 minutes and 2 GB each. Basic includes 60 minutes of embed hosting, Pro 90 and Studio 120, with no expiry date for as long as you are subscribed.',
      },
      {
        q: 'Can I sell prints through Pixally?',
        a: 'Yes. Couples can order prints and albums from their gallery, shipped to the United States and Canada.',
      },
    ],
  },
  {
    title: 'Second shooters, contractors and editing',
    items: [
      {
        q: 'How do second shooters use Pixally?',
        a: 'They create a free contractor account that works across every studio they shoot for. Second shooter and associate management is on Pro (up to 10) and Studio (unlimited), with a contractor portal on both.',
      },
      {
        q: 'Can one studio see what another pays a contractor?',
        a: 'No. A studio never sees another studio\'s records about a contractor, such as pay, jobs or ratings. The only thing shared between studios is that a booked date shows as unavailable, without saying who booked it.',
      },
      {
        q: 'How are W-9 tax forms handled?',
        a: 'A W-9 is stored as an encrypted file. Pixally does not extract, search or display the taxpayer number on it, and only the contractor, the owners and administrators of each studio that has set the contractor\'s pay rate, and the people who support the feature can open it.',
      },
      {
        q: 'Does Pixally track post-production?',
        a: 'Yes, on Pro and Studio: edits from upload through culling, assigned edits with due dates, review, client revisions and delivery.',
      },
    ],
  },
  {
    title: 'Switching to Pixally',
    items: [
      {
        q: 'Will Pixally move my data from my current CRM?',
        a: 'Yes, free on every plan: up to your last 12 months of active client projects, and your existing documents turned into Pixally templates.',
      },
      {
        q: 'When is the best time to switch?',
        a: 'Winter, when the wedding season is quiet. Pixally launches on January 1, 2027, at the start of it.',
      },
    ],
  },
  {
    title: 'Privacy',
    items: [
      {
        q: 'Does Pixally sell my data or my clients\' data?',
        a: 'No. Pixally does not sell personal information, and your clients\' information is never used for advertising: there are no advertising trackers in client galleries, client portals or contractor accounts. Our own marketing pages do use Meta\'s advertising pixel to measure our ads, which some US state laws count as "sharing" personal information. You can turn it off at pixally.com/privacy-choices.',
      },
      {
        q: 'Does Pixally use facial recognition on gallery photos?',
        a: 'No. Pixally does not perform facial recognition or any other biometric matching on the photographs in a gallery.',
      },
      {
        q: 'Are my clients\' photos used to train AI?',
        a: 'No. Pixally does not use clients\' photographs, films, messages or documents to train generally available AI models, and does not let its vendors do so.',
      },
    ],
  },
];
